function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/* eslint-disable react/button-has-type */

/** @jsxRuntime classic */

/** @jsx jsx */
import React from 'react';
import { css, jsx, keyframes } from '@emotion/core';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import * as colors from './Colors';
const LARGE_WITH_ICON = 'lg-icon';
const NORMAL_WITH_ICON = 'md-icon';
/**
 * This function is used to define a color style for the buttons
 * @param {string} name Color name, used along with the theme.
 * @param {string} outline Outline color.
 */

const themes = {
  home: {
    alpha: colors.home.frenchPass,
    // Text color
    beta: colors.home.AzureishWhite,
    // Background color
    gamma: colors.home.frenchPass,
    // Border color
    delta: colors.home.frenchPass // Text color outline

  },
  migratoryWebCitizen: {
    alpha: colors.migratoryWebCitizen.frenchPass,
    beta: colors.migratoryWebCitizen.AzureishWhite,
    gamma: colors.migratoryWebCitizen.frenchPass,
    delta: colors.migratoryWebCitizen.frenchPass
  },
  sanitarianRegistersWeb: {
    alpha: colors.sanitarianRegistersWeb.cello,
    beta: colors.sanitarianRegistersWeb.cadetBlue,
    gamma: colors.sanitarianRegistersWeb.cadetBlue,
    delta: colors.sanitarianRegistersWeb.cadetBlue
  },
  phytosanitaryWeb: {
    alpha: colors.phytosanitaryWeb.frenchPass,
    beta: colors.phytosanitaryWeb.AzureishWhite,
    gamma: colors.phytosanitaryWeb.frenchPass,
    delta: colors.phytosanitaryWeb.frenchPass
  },
  tdCarWeb: {
    alpha: colors.tdCarWeb.frenchPass,
    beta: colors.tdCarWeb.AzureishWhite,
    gamma: colors.tdCarWeb.frenchPass,
    delta: colors.tdCarWeb.frenchPass
  }
};

const colorStyle = (name, outline) => {
  const {
    theme
  } = useTheme();
  let property = {
    backgroundColor: colors[theme][name]
  };

  if (outline) {
    property = {
      border: `2px solid ${colors[theme][name]}`,
      color: colors[theme][name]
    };
  }

  return css({ ...property
  });
};

const sizes = {
  default: css({
    borderRadius: 2,
    width: 190,
    padding: '15px 0',
    cursor: 'pointer'
  }),
  [LARGE_WITH_ICON]: css({
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '12px 20px',
    cursor: 'pointer',
    fontWeight: 'bold',
    width: 170
  }),
  [NORMAL_WITH_ICON]: css({
    borderRadius: 2,
    width: 190,
    fontWeight: 'bold',
    padding: '15px 0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer'
  })
};
const SpanLargeButton = styled.span({
  fontStyle: 'normal',
  fontWeight: 'bold',
  fontSize: 13,
  lineHeight: '15px',
  textAlign: 'center',
  letterSpacing: '0.01em',
  marginTop: 8
});
const SpanNormalButton = styled.span`
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  color: 'inherit';
`;
const shimmer = keyframes`
  100% {
    transform: translateX(100%)
  }
`;
const skeletonCss = css`
  position: relative;

  &::after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translateX(-100%);
    background-image: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0,
      rgba(255, 255, 255, 0.2) 20%,
      rgba(255, 255, 255, 0.5) 60%
    );
    animation: ${shimmer} 2s infinite;
    content: '';
  }
`;
/**
 * This function is used to define a color style for the buttons
 * @param {JSON} disabledStyles Custom styles for the button when disabled.
 * @param {*} children Button text.
 * @param {string} type Button type, default: 'button'.
 * @param {string} size This prop is use to define the size of the button (NORMAL_WITH_ICON, LARGE_WITH_ICON).
 * @param {json} color Button color and outline color.
 * @param {string} outline Outline color.
 * @param {JSON} spanStyle Custom style for the span inside de button.
 * @param {boolean} disabled Determines if the button is disabled o enable.
 * @param {*} props All other supported props.
 */

const Button = /*#__PURE__*/React.memo(({
  disabledStyles,
  children,
  type = 'button',
  size,
  color,
  outline = false,
  spanStyle,
  disabled = false,
  skeleton = false,
  ...props
}) => {
  const {
    theme
  } = useTheme();
  const styles = [];
  let child = children;

  if (skeleton) {
    return jsx("button", _extends({
      css: skeletonCss
    }, props));
  }

  if (size) {
    styles.push(sizes[size]);

    if (size === LARGE_WITH_ICON) {
      const [icon, ...rest] = child;
      child = [icon, jsx(SpanLargeButton, {
        key: "lg-1",
        style: { ...spanStyle
        }
      }, rest)];
    } else if (size === NORMAL_WITH_ICON) {
      child = React.Children.map(child, (iterator, index) => {
        if (iterator === null || typeof iterator === 'string' && iterator.length === 0) {
          return '';
        }

        if (typeof iterator === 'string') {
          return jsx(SpanNormalButton, {
            key: index,
            style: { ...spanStyle
            },
            outline: outline
          }, iterator);
        }

        return /*#__PURE__*/React.cloneElement(iterator, {
          key: 'a1'
        });
      });
    }
  } else {
    styles.push(sizes.default);
    child = jsx(SpanNormalButton, {
      key: 1,
      style: { ...spanStyle
      },
      outline: outline
    }, child);
  }

  if (color) {
    styles.push(colorStyle(color, outline));
  }

  if (disabled) {
    styles.push({
      cursor: 'not-allowed'
    });

    if (!outline) {
      styles.push({
        color: `${themes[theme].alpha} !important`,
        background: `${themes[theme].beta} !important`,
        border: `2px solid ${themes[theme].beta} !important`
      });
    } else {
      styles.push({
        color: `${themes[theme].delta} !important`,
        border: `2px solid ${themes[theme].gamma} !important`
      });
    }
  }

  return jsx("button", _extends({
    css: disabledStyles || styles,
    type: type
  }, props, {
    disabled: disabled
  }), child);
});
export default Button;